<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <h2 class="body-1 mt-0 mb-3">
        <v-icon size="24" color="primary" aria-hidden="true" class="pb-1">mdi-application-brackets</v-icon>
        <span class="ml-2">{{ title }}</span>
      </h2>

      <!-- markdown is sanitized before injection (see renderedMarkdown). -->
      <div v-if="renderedMarkdown" class="vds-app-info__md" v-html="renderedMarkdown" />

      <v-simple-table v-if="params.length" dense class="mt-3">
        <template v-slot:default>
          <tbody>
            <tr v-for="(p, i) in params" :key="i">
              <td class="font-weight-medium vds-app-info__label">{{ p.label }}</td>
              <td>
                <a v-if="isUrl(p.value)" class="vds-app-info__link" :href="p.value" target="_blank" rel="noopener noreferrer">{{ p.value }}</a>
                <kvm-copyable v-else :value="p.value" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { renderMarkdown } from '@/helpers/markdown'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'

export default {
  name: 'KvmAppInfo',
  components: { KvmCopyable },
  props: {
    // The /service app_info object: { markdown, params: [{ label, value }] }.
    appInfo: { type: Object, default: null },
    // The app slug, used for the card title.
    appName: { type: String, default: '' },
  },
  computed: {
    title () {
      if (!this.appName) return 'Application'
      return this.appName.charAt(0).toUpperCase() + this.appName.slice(1)
    },
    params () {
      return (this.appInfo && this.appInfo.params) || []
    },
    // Variables are already resolved server-side; we only parse + sanitize here.
    renderedMarkdown () {
      return renderMarkdown(this.appInfo && this.appInfo.markdown)
    },
  },
  methods: {
    isUrl (v) {
      return typeof v === 'string' && /^https?:\/\//i.test(v)
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Markdown headings render below the card's <h2>, so keep them visually
// subordinate. Colour inherits the theme text token (readable in both themes).
.vds-app-info__md ::v-deep h1,
.vds-app-info__md ::v-deep h2,
.vds-app-info__md ::v-deep h3 {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 8px;
}
.vds-app-info__md ::v-deep p,
.vds-app-info__md ::v-deep ul,
.vds-app-info__md ::v-deep ol {
  margin-bottom: 8px;
}

// Inline code — readable surface in BOTH themes (the original dark-on-dark
// rgba(0,0,0,.06) was invisible in dark mode).
.vds-app-info__md ::v-deep code {
  background: rgba(0, 0, 0, 0.07);
  padding: 1px 5px;
  border-radius: 3px;
}
.theme--dark .vds-app-info__md ::v-deep code {
  background: rgba(255, 255, 255, 0.14);
}

// Fenced code blocks — give them a surface and let long lines scroll rather
// than blow out the layout. Suppress the inline-code background inside.
.vds-app-info__md ::v-deep pre {
  background: $kvm-stripe-light;
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin-bottom: 8px;
}
.vds-app-info__md ::v-deep pre code {
  background: none;
  padding: 0;
}
.theme--dark .vds-app-info__md ::v-deep pre {
  background: rgba(255, 255, 255, 0.08);
}

// Links (markdown-rendered + the param URL link) — the portal's own link colour,
// with a visible focus ring (overrides the global a:focus outline:0).
.vds-app-info__md ::v-deep a,
.vds-app-info__link {
  text-decoration: underline;
  word-break: break-all;

  &:focus-visible {
    @include kvm-focus-ring;
    border-radius: 3px;
  }
}

// Param label cell — wide on desktop, but allowed to shrink so the 2-col table
// reflows cleanly at 320px instead of forcing horizontal scroll.
.vds-app-info__label {
  width: 220px;
  vertical-align: top;
}
@media (max-width: $kvm-bp-mobile) {
  .vds-app-info__label {
    width: auto;
    min-width: 96px;
  }
}
</style>
